// Per-serving nutrition estimates from USDA FoodData Central values (fdc.json).
//
// Rules (knowledge rule `nutrient_data_source`):
//   * Every number traces to an FDC record (fdcId). No values from memory or from a model.
//   * Estimates are ranges, because brands, exact portions and cooking change the real numbers.
//   * If any ingredient that matters can't be converted to grams with FDC data, return null and
//     show nothing rather than a misleading partial number.

import fdc from './fdc.json';
import type { Recipe, RecipeIngredient } from '../recipes';
import type { IngredientId } from '../taxonomy';
import type { Substitution } from '../substitutions';

export interface Per100g { kcal: number; protein: number; carbs: number; fat: number; fiber: number; sodium: number }
interface FoodRecord {
  fdcId: number | null;
  description: string | null;
  per100g: Per100g | null;
  portions: Partial<Record<'cup' | 'tbsp' | 'tsp' | 'each' | 'slice' | 'can' | 'clove', number | null>>;
}

const FOODS = (fdc as { foods: Record<string, FoodRecord> }).foods;

/** Generic household measures that don't depend on the food. */
const FIXED_GRAMS: Partial<Record<NonNullable<RecipeIngredient['unit']>, number>> = { g: 1 };
/** Seasoning amounts too small to move an estimate. */
const NEGLIGIBLE = new Set<IngredientId>([
  'salt', 'black_pepper', 'cumin', 'paprika', 'oregano', 'chili_flakes', 'curry_powder', 'cinnamon', 'thyme', 'baking_powder',
  'garam_masala', 'turmeric', 'ground_coriander', 'bay_leaves', 'rosemary', 'sumac',
  'lemongrass', // simmered for flavour and set aside
]);

/** USDA record for an ingredient measured cooked; with none listed the estimate is withheld. */
const COOKED: Partial<Record<IngredientId, IngredientId>> = { rice: 'leftover_rice', white_rice: 'leftover_rice' };

/**
 * Condiments USDA FoodData Central has no non-branded record for. Recipes use them by the spoonful,
 * so the estimate leaves them out and lists them, rather than guessing or showing nothing.
 */
const UNSOURCED = new Set<IngredientId>(['gochujang', 'chili_bean_paste', 'red_curry_paste', 'green_curry_paste']);

/** Plus/minus applied to the point estimate. */
export const ESTIMATE_UNCERTAINTY = 0.2;

export interface Range { low: number; high: number }
export interface NutritionEstimate {
  kcal: Range;
  protein: Range;
  carbs: Range;
  fat: Range;
  fiber: Range;
  /** Point values per serving, rounded, for compact labels. The ranges are the honest figure. */
  perServing: { kcal: number; protein: number; carbs: number };
  fdcIds: number[];
  /** Ingredients left out because USDA has no record for them. */
  unsourced: IngredientId[];
}

function recordId(ing: RecipeIngredient): IngredientId {
  return ing.cooked ? COOKED[ing.id] ?? ing.id : ing.id;
}

export function gramsOf(ing: RecipeIngredient): number | null {
  if (ing.qty === undefined || !ing.unit) return null;
  if (ing.unit === 'ml') return ing.qty; // close enough for watery liquids; flagged in the range
  const fixed = FIXED_GRAMS[ing.unit];
  if (fixed) return ing.qty * fixed;
  if (ing.unit === 'pinch') return 0;
  const portion = FOODS[recordId(ing)]?.portions?.[ing.unit as 'cup'];
  return portion ? ing.qty * portion : null;
}

/**
 * Per-serving estimate for the meal as it will be cooked: optional ingredients the user's rules drop
 * are left out, and a substitute is weighed at the original ingredient's amount with its own USDA values.
 */
export function estimateRecipe(recipe: Recipe, omitted: IngredientId[] = [], swaps: Pick<Substitution, 'from' | 'to'>[] = []): NutritionEstimate | null {
  const swapFor = new Map(swaps.map((s) => [s.from, s.to]));
  const totals: Per100g = { kcal: 0, protein: 0, carbs: 0, fat: 0, fiber: 0, sodium: 0 };
  const ids: number[] = [];
  const unsourced: IngredientId[] = [];
  for (const ing of recipe.ingredients) {
    if (ing.role === 'optional' || omitted.includes(ing.id) || NEGLIGIBLE.has(ing.id)) continue;
    if (UNSOURCED.has(ing.id)) { unsourced.push(ing.id); continue; }
    if (ing.cooked && !COOKED[ing.id]) return null;
    const food = FOODS[swapFor.get(ing.id) ?? recordId(ing)];
    const grams = gramsOf(ing);
    // Unquantified pantry items (oil "for the pan", garlic to taste) make the estimate unreliable.
    if (!food?.per100g || food.fdcId === null || grams === null) return null;
    for (const k of Object.keys(totals) as (keyof Per100g)[]) totals[k] += (food.per100g[k] * grams) / 100;
    ids.push(food.fdcId);
  }
  if (!ids.length) return null;
  const range = (total: number): Range => {
    const v = total / recipe.servings;
    return { low: Math.max(0, Math.round(v * (1 - ESTIMATE_UNCERTAINTY))), high: Math.round(v * (1 + ESTIMATE_UNCERTAINTY)) };
  };
  const per = (total: number) => Math.round(total / recipe.servings);
  return { perServing: { kcal: per(totals.kcal), protein: per(totals.protein), carbs: per(totals.carbs) },
    kcal: range(totals.kcal), protein: range(totals.protein), carbs: range(totals.carbs), fat: range(totals.fat), fiber: range(totals.fiber), fdcIds: [...new Set(ids)], unsourced };
}

/** Estimate for a ranked or recommended meal, with its omissions and substitutions applied. */
export function estimateMeal(r: { recipe: Recipe; omitted: IngredientId[]; substitutions: Pick<Substitution, 'from' | 'to'>[] }): NutritionEstimate | null {
  return estimateRecipe(r.recipe, r.omitted, r.substitutions);
}

/** Compact per-serving label: calories, carbs and protein. */
export function macroLine(e: NutritionEstimate): string {
  return `${e.perServing.kcal} kcal · ${e.perServing.carbs} g carbs · ${e.perServing.protein} g protein`;
}

/** True once the FDC sync has populated data. */
export function hasNutrientData(): boolean {
  return Object.values(FOODS).some((f) => f.per100g !== null && f.fdcId !== null);
}

export function formatRange(r: Range, unit: string): string {
  return r.low === r.high ? `${r.low} ${unit}` : `${r.low} to ${r.high} ${unit}`;
}
